import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';

import { Features } from '@lessonPlanner/constants';

import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import DynamicLabelGenerateButton from '../components/molecules/DynamicLabelGenerateButton';
import AmericanQuizmaker from '../components/quizmaker/AmericanQuizmaker';
import DefaultQuizmaker from '../components/quizmaker/DefaultQuizmaker';
import LocalQuizmaker from '../components/quizmaker/LocalQuizmaker';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import { defaultCreditCost } from '../constants/credits';
import formDefaultValues from '../constants/forms/quizmaker/defaults';
import FORM_CONTENT_MAPPER_KEYS from '../constants/forms/quizmaker/formContentMapperKeys';
import { QUIZMAKER_PAGE_DESCRIPTION } from '../constants/quizmaker';
import { useGeneratePause } from '../context/generate/provider';
import PortalContext from '../context/portal/context';
import { PageKey, QuizMakerSingleProperties } from '../enums/pageKey';
import { Portal } from '../enums/portal.enum';
import { useScrollToError } from '../hooks/useScrollToError';
import SidebarLayout from '../layouts/SidebarLayout';
import QuizMakerSchema from '../schemas/quizmaker/QuizMakerSchema';
import { quizMakerSingleProperties } from '../types/page';
import { mapFormContents } from '../utils/mapFormContents';

function getDefaultValues(portal: Portal) {
  const defaultValues = formDefaultValues();
  switch (portal) {
    case Portal.PHILIPPINES:
      return defaultValues.LocalQuizmaker;
    case Portal.DEFAULT:
      return defaultValues.DefaultQuizmaker;
    case Portal.USA:
      return defaultValues.AmericanQuizmaker;
    default:
      return {};
  }
}

function getFormContentMapper(portal: Portal) {
  switch (portal) {
    case Portal.PHILIPPINES:
      return FORM_CONTENT_MAPPER_KEYS.LocalQuizmaker;
    case Portal.DEFAULT:
      return FORM_CONTENT_MAPPER_KEYS.DefaultQuizmaker;
    case Portal.USA:
      return FORM_CONTENT_MAPPER_KEYS.AmericanQuizmaker;
    default:
      return [];
  }
}

export default function QuizMaker(): JSX.Element {
  const { selectedPortal } = useContext(PortalContext);
  const isLocalPortal = selectedPortal === Portal.PHILIPPINES;
  const isDefaultPortal = selectedPortal === Portal.DEFAULT;
  const isUSAPortal = selectedPortal === Portal.USA;

  const defaultValues = getDefaultValues(selectedPortal);
  const formContentMapper = getFormContentMapper(selectedPortal);
  const schema = QuizMakerSchema(selectedPortal);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    formState: { errors },
    setValue,
    watch,
    trigger: triggerValidation,
    clearErrors,
  } = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(schema as ObjectSchema<unknown>),
  });

  const watchedValues = watch();

  const hasErrors = Object.entries(errors).length > 0;
  useScrollToError(errors);

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();
  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.quizMakerSingle];

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  const banner = (
    <GenericBanner content={QUIZMAKER_PAGE_DESCRIPTION} title="Short Quiz" />
  );

  const formContents = useMemo(() => {
    const mergedValues = mapFormContents<QuizMakerSingleProperties>(
      defaultValues,
      watchedValues,
      formContentMapper,
    );

    return {
      ...mergedValues,
      feature: Features.QUIZMAKER,
    };
  }, [watchedValues, formContentMapper, defaultValues]);

  const customFormValidator = useCallback(async () => {
    await triggerValidation();
  }, [triggerValidation]);

  return (
    <MainContent
      banner={banner}
      formContents={formContents as quizMakerSingleProperties}
      pageKey={PageKey.quizMakerSingle}
      setIsLoading={setIsLoading}
    >
      <>
        {isLocalPortal && (
          <LocalQuizmaker
            clearErrors={clearErrors}
            errors={errors}
            setValue={setValue}
            triggerValidation={triggerValidation}
            watchedValues={watchedValues}
          />
        )}

        {isDefaultPortal && (
          <DefaultQuizmaker
            clearErrors={clearErrors}
            errors={errors}
            setValue={setValue}
            triggerValidation={triggerValidation}
            watchedValues={watchedValues}
          />
        )}

        {isUSAPortal && (
          <AmericanQuizmaker
            clearErrors={clearErrors}
            errors={errors}
            setValue={setValue}
            triggerValidation={triggerValidation}
            watchedValues={watchedValues}
          />
        )}

        <div className="col-span-full">
          <div className="flex flex-col">
            <DynamicLabelGenerateButton
              isRegularDefault
              buttonText={buttonText}
              creditCost={defaultCreditCost}
              handleOnCLick={customFormValidator}
              isDisabled={isGenerateButtonDisabled}
            />
          </div>
        </div>
      </>
    </MainContent>
  );
}

QuizMaker.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
